import type { ReactNode } from 'react'

import { EmptyState } from '@/components/ui/EmptyState'
import { Pagination } from '@/components/ui/Pagination'
import type { PaginationMeta } from '@/types/api'

export interface DataTableColumn<T> {
  key: string
  header: string
  render: (row: T) => ReactNode
  sortable?: boolean
  className?: string
  filter?: ReactNode
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string | number
  loading?: boolean
  emptyMessage?: string
  sortKey?: string | null
  sortDirection?: 'asc' | 'desc'
  onSortChange?: (key: string) => void
  selectable?: boolean
  selectedKeys?: Set<string | number>
  onToggleRow?: (row: T) => void
  onToggleAll?: (rows: T[]) => void
  bulkActions?: ReactNode
  pagination?: PaginationMeta
  onPageChange?: (page: number) => void
  density?: 'default' | 'compact'
  onRowClick?: (row: T) => void
}

/**
 * Generic list surface reused by applications, exports, activity logs,
 * users, and security events — sticky header, sortable columns, optional
 * bulk-select, pagination footer. Mobile fallback is a horizontal scroll
 * (table-responsive) rather than a full stacked-card layout — a deliberate
 * scope cut for this pass, not a broken behavior.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  emptyMessage = 'No records found.',
  sortKey,
  sortDirection = 'asc',
  onSortChange,
  selectable,
  selectedKeys,
  onToggleRow,
  onToggleAll,
  bulkActions,
  pagination,
  onPageChange,
  density = 'default',
  onRowClick,
}: DataTableProps<T>) {
  const allSelected =
    !!selectable && rows.length > 0 && rows.every((row) => selectedKeys?.has(rowKey(row)))
  const anySelected = !!selectable && !!selectedKeys && selectedKeys.size > 0
  const colSpan = columns.length + (selectable ? 1 : 0)
  const hasFilters = columns.some((col) => col.filter !== undefined)

  return (
    <div>
      {anySelected && bulkActions && (
        <div className="d-flex align-items-center gap-2 p-2 bg-light border-bottom">
          <span className="fs-13 fw-medium">{selectedKeys?.size ?? 0} selected</span>
          {bulkActions}
        </div>
      )}
      <div className="table-responsive">
        <table
          className={`table table-hover mb-0 ${density === 'compact' ? 'table-sm' : ''}`.trim()}
        >
          <thead className="sticky-top bg-white">
            <tr>
              {selectable && (
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allSelected}
                    onChange={() => onToggleAll?.(rows)}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={col.className}
                  role={col.sortable ? 'button' : undefined}
                  onClick={col.sortable ? () => onSortChange?.(col.key) : undefined}
                >
                  {col.header}
                  {col.sortable && sortKey === col.key && (
                    <span className="ms-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
              ))}
            </tr>
            {hasFilters && (
              <tr>
                {selectable && <th />}
                {columns.map((col) => (
                  <th key={col.key} className="fw-normal py-1">
                    {col.filter}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="text-center py-4">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan}>
                  <EmptyState heading={emptyMessage} />
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{
                    cursor: onRowClick ? 'pointer' : undefined,
                    height: density === 'compact' ? 44 : 56,
                  }}
                >
                  {selectable && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!selectedKeys?.has(rowKey(row))}
                        onChange={() => onToggleRow?.(row)}
                        aria-label="Select row"
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={col.className}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pagination && onPageChange && pagination.total > 0 && (
        <div className="p-2 border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
          <span className="fs-12 text-muted">
            Showing {(pagination.currentPage - 1) * pagination.perPage + 1}–
            {Math.min(pagination.currentPage * pagination.perPage, pagination.total)} of {pagination.total}
            {pagination.lastPage > 1 && ` · Page ${pagination.currentPage} of ${pagination.lastPage}`}
          </span>
          <Pagination meta={pagination} onPageChange={onPageChange} />
        </div>
      )}
    </div>
  )
}
